// groupSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import {
  createGroup,
  getUserGroups,
  getGroupDetails,
  addGroupMembers,
  removeGroupMember,
  updateGroup,
  makeGroupAdmin,
  deleteGroup,
  leaveGroup,
  searchGroups
} from '../../api.js'

// =============================================
// ASYNC THUNKS FOR GROUP OPERATIONS
// =============================================

// Create group thunk
export const createGroupThunk = createAsyncThunk(
  'groups/createGroup',
  async (groupData, { rejectWithValue }) => {
    try {
      const response = await createGroup(groupData)
      return response
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to create group')
    }
  }
)

// Get user groups thunk
export const getUserGroupsThunk = createAsyncThunk(
  'groups/getUserGroups',
  async ({ page = 1, limit = 20 } = {}, { rejectWithValue }) => {
    try {
      const response = await getUserGroups(page, limit)
      return response
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch user groups')
    }
  }
)

// Get group details thunk
export const getGroupDetailsThunk = createAsyncThunk(
  'groups/getGroupDetails',
  async (groupId, { rejectWithValue }) => {
    try {
      const response = await getGroupDetails(groupId)
      return response
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch group details')
    }
  }
)

// Add group members thunk
export const addGroupMembersThunk = createAsyncThunk(
  'groups/addGroupMembers',
  async ({ groupId, memberIds }, { rejectWithValue }) => {
    try {
      const response = await addGroupMembers(groupId, memberIds)
      return { groupId, memberIds, ...response }
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to add group members')
    }
  }
)

// Remove group member thunk
export const removeGroupMemberThunk = createAsyncThunk(
  'groups/removeGroupMember',
  async ({ groupId, memberId }, { rejectWithValue }) => {
    try {
      const response = await removeGroupMember(groupId, memberId)
      return { groupId, memberId, ...response }
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to remove group member')
    }
  }
)

// Update group thunk
export const updateGroupThunk = createAsyncThunk(
  'groups/updateGroup',
  async ({ groupId, groupData }, { rejectWithValue }) => {
    try {
      const response = await updateGroup(groupId, groupData)
      return { groupId, ...response }
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to update group')
    }
  }
)

// Make group admin thunk
export const makeGroupAdminThunk = createAsyncThunk(
  'groups/makeGroupAdmin',
  async ({ groupId, memberId }, { rejectWithValue }) => {
    try {
      const response = await makeGroupAdmin(groupId, memberId)
      return { groupId, memberId, ...response }
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to make member admin')
    }
  }
)

// Delete group thunk
export const deleteGroupThunk = createAsyncThunk(
  'groups/deleteGroup',
  async (groupId, { rejectWithValue }) => {
    try {
      const response = await deleteGroup(groupId)
      return { groupId, ...response }
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to delete group')
    }
  }
)

// Leave group thunk
export const leaveGroupThunk = createAsyncThunk(
  'groups/leaveGroup',
  async (groupId, { rejectWithValue }) => {
    try {
      const response = await leaveGroup(groupId)
      return { groupId, ...response }
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to leave group')
    }
  }
)

// Search groups thunk
export const searchGroupsThunk = createAsyncThunk(
  'groups/searchGroups',
  async ({ query = '', groupType = 'all' } = {}, { rejectWithValue }) => {
    try {
      const response = await searchGroups(query, groupType)
      return response
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to search groups')
    }
  }
)

// =============================================
// GROUP SLICE
// =============================================

const groupInitialState = {
  userGroups: [],
  groupDetails: {},
  searchResults: [],
  loading: {
    createGroup: false,
    getUserGroups: false,
    getGroupDetails: false,
    addGroupMembers: false,
    removeGroupMember: false,
    updateGroup: false,
    makeGroupAdmin: false,
    deleteGroup: false,
    leaveGroup: false,
    searchGroups: false
  },
  error: {
    createGroup: null,
    getUserGroups: null,
    getGroupDetails: null,
    addGroupMembers: null,
    removeGroupMember: null,
    updateGroup: null,
    makeGroupAdmin: null,
    deleteGroup: null,
    leaveGroup: null,
    searchGroups: null
  },
  currentGroupId: null,
  pagination: {
    groups: { page: 1, hasMore: true, totalPages: 1 }
  }
}

const groupSlice = createSlice({
  name: 'groups',
  initialState: groupInitialState,
  reducers: {
    setCurrentGroup: (state, action) => {
      state.currentGroupId = action.payload
    },
    clearGroupSearchResults: state => {
      state.searchResults = []
    },
    clearGroupError: (state, action) => {
      const errorType = action.payload
      if (errorType && state.error[errorType]) {
        state.error[errorType] = null
      }
    },
    resetGroupState: () => groupInitialState
  },
  extraReducers: builder => {
    // Create group cases
    builder
      .addCase(createGroupThunk.pending, state => {
        state.loading.createGroup = true
        state.error.createGroup = null
      })
      .addCase(createGroupThunk.fulfilled, (state, action) => {
        state.loading.createGroup = false
        state.userGroups.unshift(action.payload.group)
      })
      .addCase(createGroupThunk.rejected, (state, action) => {
        state.loading.createGroup = false
        state.error.createGroup = action.payload
      })

    // Get user groups cases - FIXED TO HANDLE CORRECT RESPONSE STRUCTURE
    builder
      .addCase(getUserGroupsThunk.pending, state => {
        state.loading.getUserGroups = true
        state.error.getUserGroups = null
      })
      .addCase(getUserGroupsThunk.fulfilled, (state, action) => {
        state.loading.getUserGroups = false
        const { groups = [], pagination = {} } = action.payload

        if ((pagination.page || 1) === 1) {
          state.userGroups = groups
        } else {
          state.userGroups = [...state.userGroups, ...groups]
        }

        state.pagination.groups = {
          page: pagination.page || 1,
          hasMore: pagination.hasMore || false,
          totalPages: pagination.totalPages || 1,
          total: pagination.total || 0
        }
      })
      .addCase(getUserGroupsThunk.rejected, (state, action) => {
        state.loading.getUserGroups = false
        state.error.getUserGroups = action.payload
      })

    // Get group details cases
    builder
      .addCase(getGroupDetailsThunk.pending, state => {
        state.loading.getGroupDetails = true
        state.error.getGroupDetails = null
      })
      .addCase(getGroupDetailsThunk.fulfilled, (state, action) => {
        state.loading.getGroupDetails = false
        const group = action.payload.group
        state.groupDetails[group._id] = group
      })
      .addCase(getGroupDetailsThunk.rejected, (state, action) => {
        state.loading.getGroupDetails = false
        state.error.getGroupDetails = action.payload
      })

    // Add group members cases
    builder
      .addCase(addGroupMembersThunk.pending, state => {
        state.loading.addGroupMembers = true
        state.error.addGroupMembers = null
      })
      .addCase(addGroupMembersThunk.fulfilled, (state, action) => {
        state.loading.addGroupMembers = false
        const { groupId } = action.payload

        // Update group details if available
        if (state.groupDetails[groupId]) {
          state.groupDetails[groupId] = action.payload.group
        }

        // Update user groups list
        const groupIndex = state.userGroups.findIndex(
          group => group._id === groupId
        )
        if (groupIndex !== -1) {
          state.userGroups[groupIndex] = action.payload.group
        }
      })
      .addCase(addGroupMembersThunk.rejected, (state, action) => {
        state.loading.addGroupMembers = false
        state.error.addGroupMembers = action.payload
      })

    // Remove group member cases
    builder
      .addCase(removeGroupMemberThunk.pending, state => {
        state.loading.removeGroupMember = true
        state.error.removeGroupMember = null
      })
      .addCase(removeGroupMemberThunk.fulfilled, (state, action) => {
        state.loading.removeGroupMember = false
        const { groupId } = action.payload

        // Update group details if available
        if (state.groupDetails[groupId]) {
          state.groupDetails[groupId] = action.payload.group
        }

        // Update user groups list
        const groupIndex = state.userGroups.findIndex(
          group => group._id === groupId
        )
        if (groupIndex !== -1) {
          state.userGroups[groupIndex] = action.payload.group
        }
      })
      .addCase(removeGroupMemberThunk.rejected, (state, action) => {
        state.loading.removeGroupMember = false
        state.error.removeGroupMember = action.payload
      })

    // Update group cases
    builder
      .addCase(updateGroupThunk.pending, state => {
        state.loading.updateGroup = true
        state.error.updateGroup = null
      })
      .addCase(updateGroupThunk.fulfilled, (state, action) => {
        state.loading.updateGroup = false
        const { groupId } = action.payload
        const updatedGroup = action.payload.group

        // Update group details
        if (state.groupDetails[groupId]) {
          state.groupDetails[groupId] = updatedGroup
        }

        // Update user groups list
        const groupIndex = state.userGroups.findIndex(
          group => group._id === groupId
        )
        if (groupIndex !== -1) {
          state.userGroups[groupIndex] = updatedGroup
        }
      })
      .addCase(updateGroupThunk.rejected, (state, action) => {
        state.loading.updateGroup = false
        state.error.updateGroup = action.payload
      })

    // Make group admin cases
    builder
      .addCase(makeGroupAdminThunk.pending, state => {
        state.loading.makeGroupAdmin = true
        state.error.makeGroupAdmin = null
      })
      .addCase(makeGroupAdminThunk.fulfilled, (state, action) => {
        state.loading.makeGroupAdmin = false
        const { groupId } = action.payload

        // Update group details if available
        if (state.groupDetails[groupId]) {
          state.groupDetails[groupId] = action.payload.group
        }

        // Update user groups list
        const groupIndex = state.userGroups.findIndex(
          group => group._id === groupId
        )
        if (groupIndex !== -1) {
          state.userGroups[groupIndex] = action.payload.group
        }
      })
      .addCase(makeGroupAdminThunk.rejected, (state, action) => {
        state.loading.makeGroupAdmin = false
        state.error.makeGroupAdmin = action.payload
      })

    // Delete group cases
    builder
      .addCase(deleteGroupThunk.pending, state => {
        state.loading.deleteGroup = true
        state.error.deleteGroup = null
      })
      .addCase(deleteGroupThunk.fulfilled, (state, action) => {
        state.loading.deleteGroup = false
        const { groupId } = action.payload

        // Remove from user groups
        state.userGroups = state.userGroups.filter(
          group => group._id !== groupId
        )

        // Remove from group details
        delete state.groupDetails[groupId]

        // Clear current group if it was deleted
        if (state.currentGroupId === groupId) {
          state.currentGroupId = null
        }
      })
      .addCase(deleteGroupThunk.rejected, (state, action) => {
        state.loading.deleteGroup = false
        state.error.deleteGroup = action.payload
      })

    // Leave group cases
    builder
      .addCase(leaveGroupThunk.pending, state => {
        state.loading.leaveGroup = true
        state.error.leaveGroup = null
      })
      .addCase(leaveGroupThunk.fulfilled, (state, action) => {
        state.loading.leaveGroup = false
        const { groupId } = action.payload

        // Remove from user groups
        state.userGroups = state.userGroups.filter(
          group => group._id !== groupId
        )

        // Remove from group details
        delete state.groupDetails[groupId]

        // Clear current group if user left it
        if (state.currentGroupId === groupId) {
          state.currentGroupId = null
        }
      })
      .addCase(leaveGroupThunk.rejected, (state, action) => {
        state.loading.leaveGroup = false
        state.error.leaveGroup = action.payload
      })

    // Search groups cases
    builder
      .addCase(searchGroupsThunk.pending, state => {
        state.loading.searchGroups = true
        state.error.searchGroups = null
      })
      .addCase(searchGroupsThunk.fulfilled, (state, action) => {
        state.loading.searchGroups = false
        state.searchResults = action.payload.groups || []
      })
      .addCase(searchGroupsThunk.rejected, (state, action) => {
        state.loading.searchGroups = false
        state.error.searchGroups = action.payload
      })
  }
})

export const {
  setCurrentGroup,
  clearGroupSearchResults,
  clearGroupError,
  resetGroupState
} = groupSlice.actions

export const groupReducer = groupSlice.reducer
